import React, { Component } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  AlertTitle,
  Stack,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { getSession } from '../../auth';
import api from '../../api';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Optional: Log to backend API
    if (this.props.logToBackend) {
      this.logErrorToBackend(error, errorInfo);
    }
  }

  logErrorToBackend = async (error, errorInfo) => {
    try {
      const user = getSession();
      const errorData = {
        message: error.toString(),
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        userId: user?.profile?.id,
        enterpriseId: user?.profile?.enterprise?.id,
        componentName: this.props.componentName || 'Unknown',
        severity: 'error',
      };

      await api.post('/api/frontend-errors/', errorData, { 
        timeout: 5000 
      }).catch(err => {
        console.warn('Failed to log error to backend:', err);
      });
    } catch (err) {
      console.error('Failed to log error to backend:', err);
    }
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });

    // Call optional reset callback
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  toggleDetails = () => {
    this.setState((prevState) => ({
      showDetails: !prevState.showDetails,
    }));
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback({
          error: this.state.error,
          errorInfo: this.state.errorInfo,
          resetError: this.handleReset,
        });
      }

      // Default fallback UI
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: this.props.fullScreen ? '100vh' : '200px',
            p: 2,
          }}
        >
          <Card
            elevation={3}
            sx={{
              maxWidth: 600,
              width: '100%',
            }}
          >
            <CardContent>
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <ErrorIcon color="error" fontSize="large" />
                  <Typography variant="h5" component="h2" color="error">
                    {this.props.title || 'Algo deu errado'}
                  </Typography>
                </Box>

                <Alert severity="error">
                  <AlertTitle>Erro</AlertTitle>
                  {this.props.message || 
                    'Ocorreu um erro inesperado. Por favor, tente novamente.'}
                </Alert>

                {/* Error Details (Development) */}
                {this.state.error && (
                  <>
                    <Button
                      size="small"
                      onClick={this.toggleDetails}
                      endIcon={
                        <ExpandMoreIcon
                          sx={{
                            transform: this.state.showDetails
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)',
                            transition: 'transform 0.3s',
                          }}
                        />
                      }
                    >
                      {this.state.showDetails
                        ? 'Ocultar Detalhes'
                        : 'Mostrar Detalhes'}
                    </Button>

                    <Collapse in={this.state.showDetails}>
                      <Box
                        sx={{
                          backgroundColor: '#f5f5f5',
                          p: 2,
                          borderRadius: 1,
                          overflow: 'auto',
                          maxHeight: 300,
                        }}
                      >
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {this.state.error.toString()}
                          {'\n\n'}
                          {this.state.error.stack}
                          {'\n\nComponent Stack:'}
                          {this.state.errorInfo?.componentStack}
                        </Typography>
                      </Box>
                    </Collapse>
                  </>
                )}

                {/* Action Buttons */}
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  {this.props.showReload && (
                    <Button
                      variant="outlined"
                      onClick={() => window.location.reload()}
                    >
                      Recarregar Página
                    </Button>
                  )}
                  {this.props.showReset !== false && (
                    <Button
                      variant="contained"
                      startIcon={<RefreshIcon />}
                      onClick={this.handleReset}
                    >
                      Tentar Novamente
                    </Button>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.defaultProps = {
  logToBackend: false,
  showReset: true,
  showReload: false,
  fullScreen: false,
};

export default ErrorBoundary;
